// Hand-written: fetches the region's latest articles when source is "auto", or renders
// the editor's manual selection. The most reused section in the design.
//
// **`related-articles` was merged into this on 2026-09-04.** Both fetched articles and
// drew a row of `ArticleCard`s; what differed was the query, so it is a third `source`.
//
// `related` means what it says: the same category as the article being read, never the
// article itself, topped up with the newest in the region when the category cannot fill
// the row - two cards with a gap where the second should be looks like a fault, not an
// editorial choice. It also keeps the standard card, where two `auto` cards become the
// home page's banner pair: the design draws the pair under the intro (3883:70251) and
// ordinary cards at the foot of an article (6377:88754).
import { useEffect, useState } from 'react';
import { CtaLink, SectionShell, withHighlight } from '../primitives';
import ArticleCard from '../../components/ArticleCard';
import { api, type ApiArticle } from '../../lib/api';
import { useCurrentArticle } from '../../lib/article-context';
import { useSite } from '../../lib/site-context';
import type { NewsTeaserSection } from '../../types/sections';

export default function NewsTeaser({ data }: { data: NewsTeaserSection }) {
  const { region, locale, articlePath, t } = useSite();
  // On an article page this section is part of the article's own layout, so the article
  // the reader is already looking at must not appear in it.
  const current = useCurrentArticle();
  const manual = (data.articles ?? []) as unknown as ApiArticle[];
  const [items, setItems] = useState<ApiArticle[]>(manual);

  const related = data.source === 'related';
  const limit = data.limit ?? (related ? 2 : 4);
  const currentCategory = current?.category?.slug;

  useEffect(() => {
    if (data.source === 'manual') { setItems(manual); return; }
    let cancelled = false;
    const exclude = current ? [current.slug] : undefined;

    const load = async () => {
      if (!related) return api.articles(region.code, locale, { pageSize: limit, exclude });

      const sameCategory = currentCategory
        ? await api.articles(region.code, locale, { pageSize: limit, category: currentCategory, exclude })
        : [];
      if (sameCategory.length >= limit) return sameCategory;

      const topUp = await api.articles(region.code, locale, {
        pageSize: limit,
        exclude: [...(exclude ?? []), ...sameCategory.map((a) => a.slug)]
      });
      return [...sameCategory, ...topUp].slice(0, limit);
    };

    load()
      .then((res) => { if (!cancelled) setItems(res); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region.code, locale, data.source, limit, current?.slug, currentCategory]);

  return (
    <SectionShell settings={data.settings} name="news-teaser">
      {/* Figma 6377:86962 - heading, a four-up row on a 16px gutter, then the CTA
          below the row rather than beside the heading. The heading is the file's h3
          style (3852:65327): 34px at regular weight, not the shared 46px `Heading`. */}
      {data.heading ? <h2 className="max-w-4xl text-h3 text-ink">{withHighlight(data.heading)}</h2> : null}

      {items.length ? (
        <div className={`grid gap-4 sm:grid-cols-2 ${related ? '' : 'lg:grid-cols-4'}`}>
          {items.map((article, i) => (
            <ArticleCard
              key={article.slug}
              article={article}
              href={articlePath(article.slug)}
              // Two cards are the home page's short banner pair under the intro
              // (3883:70251): two grid columns each and a third of the block's height.
              // Four stay quarters at the design's full 444px.
              size={!related && items.length <= 2 ? 'banner' : 'quarter'}
              // In that pair the design fills the second card blue - Figma's
              // `Active Half size`. A card with a cover photo keeps the photo instead;
              // the two treatments cannot both occupy the card.
              active={!related && items.length === 2 && i === 1}
            />
          ))}
        </div>
      ) : (
        <p className="text-body-sm text-ink-faint">{t('news.empty')}</p>
      )}

      {data.cta ? <div className="pt-4"><CtaLink value={data.cta} /></div> : null}
    </SectionShell>
  );
}
